import { useState, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '@/lib/auth'
import { api } from '@/lib/api'
import { Button, Field, Input, Modal } from './ui'
import { ThemeToggle } from './ThemeToggle'

export function AppShell({ children }: { children: ReactNode }) {
  const { user, setUser, signOut } = useAuth()
  const navigate = useNavigate()
  const [editingProfile, setEditingProfile] = useState(false)
  const [name, setName] = useState(user?.displayName ?? '')
  const [saving, setSaving] = useState(false)

  async function handleSignOut() {
    await signOut()
    navigate('/login', { replace: true })
  }

  async function saveProfile() {
    setSaving(true)
    try {
      const res = await api.updateProfile(name.trim())
      setUser(res.user)
      setEditingProfile(false)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="min-h-dvh">
      <header className="sticky top-0 z-30 border-b border-line bg-ink-950/70 backdrop-blur-xl backdrop-saturate-150">
        <div className="mx-auto flex h-14 max-w-6xl items-center gap-2 px-4">
          <Link to="/" className="flex items-center gap-2.5 font-display text-[15px] text-ink-100">
            <span aria-hidden className="grid size-7 place-items-center rounded-[9px] bg-gold-500 text-[11px] font-bold text-on-accent">20</span>
            <span className="hidden font-semibold tracking-tight sm:inline">Campaign Hub</span>
          </Link>

          <Link
            to="/rules"
            className="rounded-lg px-2.5 py-1.5 text-sm text-ink-300 transition-colors hover:bg-ink-800 hover:text-ink-100"
          >
            Rules
          </Link>

          <div className="ml-auto flex items-center gap-2">
            <ThemeToggle />
            <button
              onClick={() => { setName(user?.displayName ?? ''); setEditingProfile(true) }}
              className="rounded-lg px-2.5 py-1.5 text-sm text-ink-300 transition-colors hover:bg-ink-800 hover:text-ink-100"
            >
              {user?.displayName}
            </button>
            <Button variant="ghost" size="sm" onClick={handleSignOut}>Sign out</Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-6 sm:py-8">{children}</main>

      <Modal open={editingProfile} title="Your profile" onClose={() => setEditingProfile(false)}>
        <div className="space-y-4">
          <Field label="Display name">
            {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} maxLength={60} />}
          </Field>
          <p className="text-xs text-ink-500">Signed in as {user?.email}</p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setEditingProfile(false)}>Cancel</Button>
            <Button variant="primary" busy={saving} disabled={!name.trim()} onClick={saveProfile}>Save</Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
